// 056A Adolescente Mujer / 056B Adolescente Hombre (Anexo 1 MSP, SNS-MSP/HCU-form.056A/2021 y 056B/2021; páginas 198-201 del PDF, spec en
// docs/hce-anexos/056ab-adolescente-curvas-tanner.md). Son láminas de curvas para marcar puntos a mano: el equivalente digital toma peso y
// talla (IMC = kg/m²), el estadio de Tanner de la lámina de reverso y dibuja las cuatro curvas del papel (Peso/Edad, Talla/Edad, IMC/Edad y
// Velocidad de crecimiento) sobre el historial del paciente — ver `GraficasAdolescente.tsx`.
//
// Equivalencia funcional, no facsímil. Decisiones:
// - El papel no trae casillas para el estadio de Tanner ("determine el estadio del desarrollo puberal de acuerdo a las gráficas al reverso"):
//   se captura I-V por carácter, con la descripción impresa en cada estadio (mujer: mama y vello púbico; hombre: vello púbico, genitales
//   —escroto y pene— y, además, volumen (ml) y diámetro mayor (cm) del testículo, que la lámina de hombres imprime).
// - Peso/Edad NCHS/2000 (percentiles), Talla/Edad OMS 2007 e IMC/Edad OMS 2007 (desviaciones estándar): datos oficiales en graficos/datos.
//   Velocidad de crecimiento: los puntos del paciente sobre P3, P25, P50 y P97 de la tabla de la Fundación Orbegozo (2004); P10, P75 y P90, que el
//   papel dibuja, no están en ninguna tabla oficial y no se inventan.
// - FHIR: `Observation` de peso y talla (perfil vital-signs, mismo timestamp: de ahí sale el IMC de la curva) y un `Observation` "Estadios de
//   Tanner" con un `component` por carácter. Todo va además como `Par[]` al PDF firmado (las gráficas no se dibujan en el PDF).
import type { Encounter, Observation, ObservationComponent, Patient, Reference } from '@medplum/fhirtypes';
import { paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import { imcDe } from './calculosClinicosDatos';
import { aNumero, type TomaSignos } from './signosVitalesToma';
import type { Par } from './index';

export type SexoAdolescente = 'female' | 'male';

export const SISTEMA_TANNER = 'http://logiacare.ec/fhir/estadios-tanner';

/** Estadios I-V; un estadio sin texto impreso (solo dibujo en la lámina) queda con su número. */
const estadios = (descripciones: readonly string[]): OpcionCampo[] =>
  descripciones.map((d, i) => ({ valor: ['I', 'II', 'III', 'IV', 'V'][i]!, etiqueta: d ? `${['I', 'II', 'III', 'IV', 'V'][i]} — ${d}` : ['I', 'II', 'III', 'IV', 'V'][i]! }));

// Textos de cada estadio, como los imprime la lámina de reverso ("Modificado de E. Sileo"). El estadio I de mama es solo dibujo: sin texto.
const MAMA = estadios([
  '',
  'Aparición del botón mamario primario y alargamiento de areola',
  'Crecimiento del seno visible, con tejido glandular palpable. Pigmentación del mamelón y la areola',
  'Elevación de la areola y el mamelón formando una prominencia en relación al resto de la glándula',
  'Glándula mamaria adulta con retorno de areola a la superficie glandular, quedando formado el pezón',
]);
const VELLO_MUJER = estadios([
  'Aspecto infantil',
  'Crecimiento de vello largo y pigmentado o levemente rizado en labios o monte de Venus',
  'El vello más oscuro y espeso se extiende sobre el monte de Venus y ligeramente sobre el pubis',
  'Vello adulto en calidad y cantidad pero de topografía menos extendida',
  'Vello de tipo adulto en forma de triángulo de base superior, extendiéndose a la región superior interna de muslos',
]);
const VELLO_HOMBRE = estadios([
  'Vello púbico ausente',
  'Algunos vellos ubicados en la base del pene, largos, ligeramente pigmentados, lacios o levemente rizados',
  'El vello es más oscuro, grueso y rizado y se extiende sobre el pubis',
  'El vello más grueso y rizado invade el área del pubis, sin llegar al estado adulto',
  'Vello recubre el escroto y se extiende hacia la cara interna de los muslos y hacia la línea alba',
]);
const GENITALES = estadios([
  'Testículos V: < 3 ml. DM: < 2,5 cm',
  'Escroto: crecimiento con piel delgada, laxa, elástica, rosada, transparente. Testículo: V: 4-6 ml. DM: 2,5 a 3,2 cm. Pene sin cambios',
  'Escroto: crecimiento y pigmentación oscura. Testículo: V: 8-10 ml. DM: 3,3-4 cm. Pene: crecimiento en longitud',
  'Escroto: crecimiento y pigmentación más marcada hasta el estado adulto. Testículo: V: 12-15 ml. DM: 4,1-4,5 cm. Pene: crecimiento en longitud y diámetro. Desarrollo del glande',
  'Testículo: V: 20-25 ml. DM: 5 cm. Pene: tamaño definitivo',
]);

const lista = (clave: string, etiqueta: string, opciones: readonly OpcionCampo[]): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones });

export const CAMPOS_MEDICION: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de la medición (si es distinta de hoy)', tipo: 'fecha' },
  { clave: 'peso', etiqueta: 'Peso (kg)', tipo: 'numero' },
  { clave: 'talla', etiqueta: 'Talla (cm)', tipo: 'numero' },
];

export const CAMPOS_TANNER: Readonly<Record<SexoAdolescente, readonly CampoSimple[]>> = {
  female: [lista('mamas', 'Mama', MAMA), lista('vello', 'Vello púbico', VELLO_MUJER)],
  male: [
    lista('vello', 'Vello púbico', VELLO_HOMBRE),
    lista('genitales', 'Genitales (escroto, testículo y pene)', GENITALES),
    { clave: 'volumenTesticular', etiqueta: 'Volumen testicular (ml)', tipo: 'numero' },
    { clave: 'diametroTesticular', etiqueta: 'Diámetro mayor del testículo (cm)', tipo: 'numero' },
  ],
};

export interface Estado056ab {
  readonly medicion: Valores;
  readonly tanner: Valores;
}

export function estadoInicial056ab(sexo: SexoAdolescente): Estado056ab {
  return { medicion: valoresVacios(CAMPOS_MEDICION), tanner: valoresVacios(CAMPOS_TANNER[sexo]) };
}

// Mismo criterio numérico que las Observations de peso y talla (`aNumero`: solo dígitos y decimal): lo que se ve es lo que se guarda.
const numeroDe = aNumero;

// El IMC vive en `calculosClinicosDatos.ts` (compartido con 002/003/057/113/028C); se re-exporta para no romper importaciones.
export { imcDe };

/** Toma de peso y talla para `observacionesDeToma` (solo se crea lo que se lea como número). */
export function tomaDe056ab(e: Estado056ab): TomaSignos {
  // `observacionesDeToma` usa la fecha solo si trae también hora: a mediodía, como el Tanner de la misma consulta.
  const fecha = e.medicion.fecha || undefined;
  return { fecha, hora: fecha ? '12:00' : undefined, peso: e.medicion.peso, talla: e.medicion.talla };
}

/** `Observation` "Estadios de Tanner" con un `component` por carácter capturado; `undefined` si no se anotó ninguno. */
export function observacionTanner(e: Estado056ab, sexo: SexoAdolescente, subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahoraIso: string): Observation | undefined {
  const t = e.tanner;
  const componente = (codigo: string, display: string, valorEstadio: string | undefined): ObservationComponent | undefined =>
    valorEstadio ? { code: { coding: [{ system: SISTEMA_TANNER, code: codigo, display }] }, valueString: valorEstadio } : undefined;
  const cantidad = (codigo: string, display: string, v: string | undefined, unidad: string, ucum: string): ObservationComponent | undefined => {
    const n = numeroDe(v);
    return n === undefined ? undefined : { code: { coding: [{ system: SISTEMA_TANNER, code: codigo, display }] }, valueQuantity: { value: n, unit: unidad, system: 'http://unitsofmeasure.org', code: ucum } };
  };
  const componentes = (
    sexo === 'female'
      ? [componente('mamas', 'Desarrollo mamario', t.mamas), componente('vello-pubico', 'Vello púbico', t.vello)]
      : [
          componente('vello-pubico', 'Vello púbico', t.vello),
          componente('genitales', 'Desarrollo genital', t.genitales),
          cantidad('volumen-testicular', 'Volumen testicular', t.volumenTesticular, 'ml', 'mL'),
          cantidad('diametro-mayor-testicular', 'Diámetro mayor del testículo', t.diametroTesticular, 'cm', 'cm'),
        ]
  ).filter((c): c is ObservationComponent => c !== undefined);
  if (componentes.length === 0) return undefined;
  const efectivo = e.medicion.fecha && /^\d{4}-\d{2}-\d{2}$/.test(e.medicion.fecha) ? new Date(`${e.medicion.fecha}T12:00:00`).toISOString() : ahoraIso;
  return {
    resourceType: 'Observation',
    status: 'final',
    code: { coding: [{ system: SISTEMA_TANNER, code: 'tanner', display: 'Estadios de Tanner' }] },
    subject,
    encounter,
    effectiveDateTime: efectivo,
    component: componentes,
  };
}

/** Serializa medición, IMC y Tanner a `Par[]` (PDF firmado), sin vacíos. */
export function paresDe056ab(e: Estado056ab, sexo: SexoAdolescente): Par[] {
  const imc = imcDe(e.medicion.peso, e.medicion.talla);
  return [
    ...paresDeCampos(CAMPOS_MEDICION, e.medicion),
    ...(imc !== undefined ? [{ pregunta: 'IMC (kg/m²)', respuesta: String(imc) }] : []),
    ...paresDeCampos(CAMPOS_TANNER[sexo], e.tanner, 'Estadios de Tanner — '),
  ].filter((p) => p.respuesta.trim() !== '');
}
